import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import * as dateFns from 'date-fns';

import LegacyApi from '@deezer/next/services/api/Legacy';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {Stack, Text, VStack} from '@tempo/core';

import TopArtists from '@deezer/next/features/Analytic/sections/TopArtists';
import TopTracks from '@deezer/next/features/Analytic/sections/TopTracks';
import GlobalStreams from '@deezer/next/features/Analytic/sections/GlobalStreams';
import TrendingArtists from '@deezer/next/features/Analytic/sections/TrendingArtists';
import TrendingTracks from '@deezer/next/features/Analytic/sections/TrendingTracks';
import {useCountryOptions} from '@deezer/next/features/Analytic/hooks/useCountryOptions';
import {useAnalyticsFilters} from '@deezer/next/features/Analytic/hooks/useAnalyticsFilters';
import {useToast} from '@deezer/next/shared/hooks/useToast';
import {getErrorContent} from '@deezer/next/services/api/utils';
import type {
	AnalyticContexts,
	AnalyticTimeseries,
	AnalyticTopArtists,
	AnalyticTopTracks,
	AnalyticTrendingArtists,
} from '@deezer/next/services/api/Legacy/requests/analytic/types';
import type {AnalyticTrendingTracks} from '@deezer/next/services/api/Starmania/requests/analytic/types';
import {getUTCDate} from '@deezer/next/shared/utils/date';
import type {RangeSelector} from '@components/Charts/StreamsChart/RangeSelectors';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import HeaderRow from '@components/PageElements/PageHeader/HeaderRow';
import CountryFilter from '@deezer/next/features/Analytic/components/CountryFilter';
import OverviewContextsChart from '@deezer/next/features/Analytic/sections/OverviewContextsChart';
import SearchBar from '@components/Navigation/SearchBar';
import SearchMode from '@deezer/next/shared/utils/SearchMode';
import {useUser} from '@deezer/next/contexts/main/hooks';
import {isArtistMarketing, isProvider} from '@deezer/next/shared/utils/user';
import {
	type OnceCallback,
	useAnalytics,
} from '@deezer/next/features/Analytic/hooks/useAnalytics';
import {useOnceCallbacks} from '@deezer/next/shared/hooks/useOnceCallbacks';

import {PROVIDER_OVERVIEW_KEYS} from '../keys/providerKeys';

const ProviderOverview = () => {
	const {t} = useTranslation('d4c');
	const user = useUser();
	const toast = useToast({uniqueById: true});

	const {selectedCountry, setFilters} = useAnalyticsFilters();
	const {countryList} = useCountryOptions();

	const shouldLoadTrending = isProvider(user) || isArtistMarketing(user);

	const rangeButtons: RangeSelector[] = useMemo(() => {
		const endDate = getUTCDate();

		return [
			{
				id: '7d',
				text: t('analyticshome_text_Xdays_d4c-web', {sprintf: [7], count: 7}),
				startDate: dateFns.subDays(endDate, 7),
				endDate,
			},
			{
				id: '1m',
				text: t('analyticshome_text_Xmonths_d4c-web', {sprintf: [1], count: 1}),
				startDate: dateFns.subMonths(endDate, 1),
				endDate,
			},
			{
				id: '3m',
				text: t('analyticshome_text_Xmonths_d4c-web', {sprintf: [3], count: 3}),
				startDate: dateFns.subMonths(endDate, 3),
				endDate,
			},
			{
				id: '6m',
				text: t('analyticshome_text_Xmonths_d4c-web', {sprintf: [6], count: 6}),
				startDate: dateFns.subMonths(endDate, 6),
				endDate,
			},
			{
				id: '1y',
				text: t('analyticshome_text_Xyears_d4c-web', {sprintf: [1], count: 1}),
				startDate: dateFns.subYears(endDate, 1),
				endDate,
			},
		];
	}, [t]);

	const [activeRange, setActiveRange] = useState<RangeSelector>(
		rangeButtons[0],
	);

	const normalizedCountry = useMemo(
		() => selectedCountry.toLowerCase(),
		[selectedCountry],
	);

	const timeseriesParams = useMemo(
		() => ({
			country: normalizedCountry,
			dateFrom: dateFns.format(activeRange.startDate, 'yyyy-MM-dd'),
			dateTo: dateFns.format(activeRange.endDate, 'yyyy-MM-dd'),
		}),
		[normalizedCountry, activeRange.startDate, activeRange.endDate],
	);

	const handleCountryFilterChanged = useCallback(
		(countryCode: string) => {
			setFilters({
				selectedCountry: countryCode.toLowerCase() || 'ww',
			});
		},
		[setFilters],
	);

	const handleError = useCallback<OnceCallback>(
		errorCode => {
			const {title, description, status} = getErrorContent(errorCode, t);

			toast({
				title,
				description,
				status,
			});
		},
		[t, toast],
	);

	const {onErrorOnce} = useOnceCallbacks({
		onError: handleError,
		resetKey: `${normalizedCountry}-${timeseriesParams.dateFrom}-${timeseriesParams.dateTo}-${shouldLoadTrending}`,
	});

	const topArtists = useAnalytics<AnalyticTopArtists>({
		key: PROVIDER_OVERVIEW_KEYS.topArtists({country: normalizedCountry}),
		fetcher: async () => {
			const response =
				await LegacyApi.analytic.getTopArtists(normalizedCountry);
			return response.data;
		},
		onError: onErrorOnce,
	});

	const topTracks = useAnalytics<AnalyticTopTracks>({
		key: PROVIDER_OVERVIEW_KEYS.topTracks({country: normalizedCountry}),
		fetcher: async () => {
			const response = await LegacyApi.analytic.getTopTracks(normalizedCountry);
			return response.data;
		},
		onError: onErrorOnce,
	});

	const trendingArtists = useAnalytics<AnalyticTrendingArtists>({
		key: shouldLoadTrending
			? PROVIDER_OVERVIEW_KEYS.trendingArtists({country: normalizedCountry})
			: null,
		fetcher: async () => {
			const response =
				await LegacyApi.analytic.getTrendingArtists(normalizedCountry);
			return response.data;
		},
		onError: onErrorOnce,
	});

	const trendingTracks = useAnalytics<AnalyticTrendingTracks>({
		key: shouldLoadTrending
			? PROVIDER_OVERVIEW_KEYS.trendingTracks({country: normalizedCountry})
			: null,
		fetcher: async () => {
			const response =
				await StarmaniaApi.analytic.getTrendingTracks(normalizedCountry);
			return response.data;
		},
		onError: onErrorOnce,
	});

	const timeseries = useAnalytics<AnalyticTimeseries>({
		key: PROVIDER_OVERVIEW_KEYS.timeseries(timeseriesParams),
		fetcher: async () => {
			const response = await LegacyApi.analytic.getTimeseries(timeseriesParams);
			return response.data;
		},
		onError: onErrorOnce,
	});

	const streamingContext = useAnalytics<AnalyticContexts>({
		key: PROVIDER_OVERVIEW_KEYS.streamingContexts({country: normalizedCountry}),
		fetcher: async () => {
			const response = await LegacyApi.analytic.getContexts(normalizedCountry);
			return response.data;
		},
		onError: onErrorOnce,
	});

	const handleStreamRangeChange = useCallback((range: RangeSelector) => {
		setActiveRange(range);
	}, []);

	return (
		<PageLayout
			header={
				<>
					<TitleRow
						title="Analytics"
						subtitle={t(
							'analyticshome_text_yourcatalogforthelastXdays_d4c-web',
							{
								sprintf: [7],
							},
						)}
						rightElements={<SearchBar searchMode={SearchMode.analytic} />}
					/>
					<HeaderRow
						leftElements={
							<Stack
								direction={{base: 'column', sm: 'row'}}
								spacing={{base: 'spacing.xs', sm: 'spacing.s'}}
								alignItems="center"
							>
								<Text width={{base: '100%', sm: '40%'}}>
									{t('analyticshome_title_seeresultsfrom_d4c-web')}
								</Text>
								<CountryFilter
									className="country-filter"
									data-testid="country-filter"
									value={selectedCountry}
									countryList={countryList}
									onChange={handleCountryFilterChanged}
									size="small"
								/>
							</Stack>
						}
					/>
				</>
			}
		>
			<VStack spacing={0} alignItems="initial">
				{shouldLoadTrending && (
					<>
						<TrendingArtists
							trendingArtist={trendingArtists.data?.trendingArtist}
							isLoading={
								trendingArtists.isLoading || trendingArtists.isRefreshing
							}
							isError={trendingArtists.isError}
						/>

						<TrendingTracks
							trendingTracks={trendingTracks.data?.trendingTracks}
							isLoading={
								trendingTracks.isLoading || trendingTracks.isRefreshing
							}
							isError={trendingTracks.isError}
						/>
					</>
				)}

				<TopArtists
					topArtists={topArtists.data?.topArtists}
					isLoading={topArtists.isLoading || topArtists.isRefreshing}
					isError={topArtists.isError}
				/>

				<TopTracks
					topTracks={topTracks.data?.topTracks}
					isLoading={topTracks.isLoading || topTracks.isRefreshing}
					isError={topTracks.isError}
				/>

				<GlobalStreams
					data={timeseries.data?.timeseriesProvider}
					activeRange={activeRange}
					onRangeChange={handleStreamRangeChange}
					rangeButtons={rangeButtons}
					isLoading={timeseries.isLoading || timeseries.isRefreshing}
					isError={timeseries.isError}
				/>

				<OverviewContextsChart
					contextsDeezer={streamingContext.data?.contextsDeezer}
					contextsProvider={streamingContext.data?.contextsProvider}
					isLoading={
						streamingContext.isLoading || streamingContext.isRefreshing
					}
					isError={streamingContext.isError}
				/>
			</VStack>
		</PageLayout>
	);
};

export default ProviderOverview;
